import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';

const categories = ['Technology', 'Marketing', 'Design', 'HR', 'Finance', 'Education', 'Healthcare', 'Sales', 'Legal', 'Other'];
const jobTypes   = ['Full Time', 'Part Time'];
const experiences = ['Freshers', 'Experience', '5+ experience'];


const initial = {
  title: '', company: '', location: '', type: 'Full Time',
  category: 'Technology', salary: '', experience: 'Mid',
  description: '', requirements: '', benefits: '', skills: '',
  deadline: '', vacancies: 1, tag: 'New',
};

export default function PostJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm]       = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Redirect if not employer/admin
  if (!user) return (
    <div className="jl-empty" style={{ margin: '80px auto', maxWidth: 500 }}>
      <i className="fa fa-lock"></i>
      <h5>Sign In Required</h5>
      <p>You must be signed in as an employer to post a job.</p>
      <a href="/login" className="btn-grad mt-3">Sign In</a>
    </div>
  );

  if (user.role === 'jobseeker') return (
    <div className="jl-empty" style={{ margin: '80px auto', maxWidth: 500 }}>
      <i className="fa fa-exclamation-triangle"></i>
      <h5>Employer Account Required</h5>
      <p>Only employers can post jobs. Please register as an employer.</p>
      <a href="/login" className="btn-grad mt-3">Register as Employer</a>
    </div>
  );

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.company || !form.location || !form.description || !form.deadline)
      return setError('Please fill all required fields');
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch('http://localhost:5001/api/jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...form,
          requirements: form.requirements.split('\n').filter(Boolean),
          benefits:     form.benefits.split('\n').filter(Boolean),
          skills:       form.skills.split(',').map(s => s.trim()).filter(Boolean),
          vacancies:    Number(form.vacancies),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess('Job posted successfully!');
      setTimeout(() => navigate('/job-list'), 1500);
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Cannot connect to server. Make sure the backend is running.'
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Post a Job" breadcrumb="Post a Job" />
      <section className="section-pad">
        <div className="container" style={{ maxWidth: 900 }}>
          <div className="section-header">
            <span className="section-badge">Employer Dashboard</span>
            <h2 className="section-title">Create a Job Listing</h2>
            <p className="section-sub">Fill in the details below to attract the best candidates</p>
          </div>

          {error   && <div className="auth-alert error mb-4"><i className="fa fa-exclamation-circle me-2"></i>{error}</div>}
          {success && <div className="auth-alert success mb-4"><i className="fa fa-check-circle me-2"></i>{success}</div>}

          <form onSubmit={handleSubmit} className="post-job-form">

            {/* Basic Info */}
            <div className="post-job-section">
              <h5 className="post-job-section-title"><i className="fa fa-briefcase me-2 text-primary"></i>Basic Information</h5>
              <div className="post-job-grid">
                <div className="contact-field">
                  <label>Job Title <span className="required">*</span></label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-heading"></i>
                    <input type="text" placeholder="e.g. Software Engineer" value={form.title} onChange={e => set('title', e.target.value)} required />
                  </div>
                </div>
                <div className="contact-field">
                  <label>Company Name <span className="required">*</span></label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-building"></i>
                    <input type="text" placeholder="e.g. TechCorp Inc." value={form.company} onChange={e => set('company', e.target.value)} required />
                  </div>
                </div>
                <div className="contact-field">
                  <label>Location <span className="required">*</span></label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-map-marker-alt"></i>
                    <input type="text" placeholder="e.g. New York, USA or Remote" value={form.location} onChange={e => set('location', e.target.value)} required />
                  </div>
                </div>
                <div className="contact-field">
                  <label>Salary Range</label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-dollar-sign"></i>
                    <input type="text" placeholder="e.g. ₹80k - ₹120k" value={form.salary} onChange={e => set('salary', e.target.value)} />
                  </div>
                </div>
                <div className="contact-field">
                  <label>Application Deadline <span className="required">*</span></label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-calendar-alt"></i>
                    <input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} required />
                  </div>
                </div>
                <div className="contact-field">
                  <label>Number of Vacancies</label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-users"></i>
                    <input type="number" min="1" value={form.vacancies} onChange={e => set('vacancies', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            {/* Classification */}
            <div className="post-job-section">
              <h5 className="post-job-section-title"><i className="fa fa-tags me-2 text-primary"></i>Classification</h5>
              <div className="post-job-grid">
                <div className="contact-field">
                  <label>Job Type</label>
                  <div className="contact-input-wrap">
                    <i className="far fa-clock"></i>
                    <select value={form.type} onChange={e => set('type', e.target.value)}>
                      {jobTypes.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="contact-field">
                  <label>Category</label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-th-large"></i>
                    <select value={form.category} onChange={e => set('category', e.target.value)}>
                      {categories.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="contact-field">
                  <label>Experience Level</label>
                  <div className="contact-input-wrap">
                    <i className="fa fa-chart-bar"></i>
                    <select value={form.experience} onChange={e => set('experience', e.target.value)}>
                      {experiences.map(x => <option key={x}>{x}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="post-job-section">
              <h5 className="post-job-section-title"><i className="fa fa-align-left me-2 text-primary"></i>Job Details</h5>
              <div className="contact-field mb-4">
                <label>Job Description <span className="required">*</span></label>
                <div className="contact-input-wrap textarea">
                  <i className="fa fa-file-alt"></i>
                  <textarea rows="5" placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..." value={form.description} onChange={e => set('description', e.target.value)} required></textarea>
                </div>
              </div>
              <div className="post-job-grid">
                <div className="contact-field">
                  <label>Requirements <span style={{ color: '#888', fontWeight: 400 }}>(one per line)</span></label>
                  <div className="contact-input-wrap textarea">
                    <i className="fa fa-list-ul"></i>
                    <textarea rows="4" placeholder="5+ years experience&#10;Bachelor's degree&#10;Strong communication skills" value={form.requirements} onChange={e => set('requirements', e.target.value)}></textarea>
                  </div>
                </div>
                <div className="contact-field">
                  <label>Benefits <span style={{ color: '#888', fontWeight: 400 }}>(one per line)</span></label>
                  <div className="contact-input-wrap textarea">
                    <i className="fa fa-gift"></i>
                    <textarea rows="4" placeholder="Health insurance&#10;Remote work&#10;Annual bonus" value={form.benefits} onChange={e => set('benefits', e.target.value)}></textarea>
                  </div>
                </div>
              </div>
              <div className="contact-field mt-3">
                <label>Required Skills <span style={{ color: '#888', fontWeight: 400 }}>(comma separated)</span></label>
                <div className="contact-input-wrap">
                  <i className="fa fa-tools"></i>
                  <input type="text" placeholder="React, Node.js, MongoDB, AWS" value={form.skills} onChange={e => set('skills', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="post-job-section" style={{ background: 'linear-gradient(135deg,#f8faff,#eef4ff)', border: '1px solid #d0e4ff' }}>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 48, height: 48, background: '#e8f0ff', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0066FF', fontSize: 20 }}>
                    <i className="fa fa-crown"></i>
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, color: '#1a1a2e' }}>Boost Your Job Listing</h6>
                    <p style={{ margin: 0, fontSize: 13, color: '#666' }}>Upgrade your plan to get featured placement and more applicants</p>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  {[{ name: 'Free', color: '#0066FF', bg: '#e8f0ff' }, { name: 'Premium', color: '#f59e0b', bg: '#fff8e6' }, { name: 'Super Premium', color: '#8b5cf6', bg: '#f3eeff' }].map((p, i) => (
                    <Link key={i} to="/pricing" style={{ background: p.bg, color: p.color, padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, textDecoration: 'none', border: `1px solid ${p.color}22` }}>
                      {p.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="d-flex gap-3 justify-content-end mt-4">
              <button type="button" className="btn-outline-grad" onClick={() => setForm(initial)}>
                <i className="fa fa-redo me-2"></i>Reset
              </button>
              <button type="submit" className="btn-grad px-5" disabled={loading}>
                {loading
                  ? <><i className="fa fa-spinner fa-spin me-2"></i>Posting...</>
                  : <><i className="fa fa-paper-plane me-2"></i>Post Job</>}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}
